import type { RecordView } from '@kit/sync/types/record-view';
import type { Item } from '@/entities/item/schemas/item';
import { categorySchema } from '@/entities/shared/category/category';
import { CATEGORY_LABELS } from '@/entities/shared/category/category-labels';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { Card } from '@/shared/ui/card/card';
import { CardContent } from '@/shared/ui/card/card-content';
import { CardHeader } from '@/shared/ui/card/card-header';
import { CardTitle } from '@/shared/ui/card/card-title';

type CategoryCoverageProps = {
  readonly items: readonly RecordView<Item>[];
};

/** How many items each category has; empty ones are dashed (nothing of it yet). */
export function CategoryCoverage({ items }: CategoryCoverageProps) {
  const { t } = useTranslation();
  const counts = new Map<string, number>();
  for (const view of items) counts.set(view.data.category, (counts.get(view.data.category) ?? 0) + 1);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('By category')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {categorySchema.options.map(function chip(category) {
          const { icon: Icon, label } = CATEGORY_LABELS[category];
          const count = counts.get(category) ?? 0;
          return (
            <span
              key={category}
              title={count ? undefined : t('You have nothing in this category yet')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
                count ? 'bg-background' : 'border-dashed text-muted-foreground',
              )}
            >
              <Icon className="size-3.5" />
              {t(label)}
              <span className="text-muted-foreground tabular-nums">{count}</span>
            </span>
          );
        })}
      </CardContent>
    </Card>
  );
}
